import { useMemo } from "react";

import VideoIcon from "@assets/icons/video.svg";
import UserIcon from "@assets/icons/user.svg";

import { MeetingSubtype } from "@enums/meetings.enum";
import { IMeetingUpdatePayload } from "@interfaces/meeting.interface";
import { MeetingSubtypeSelect } from "@components/Meetings";
import { ButtonSelectorWithCustomSelect } from "@ui/ButtonSelectorWithCustomSelect";
import { cn } from "@utils/tailwind.util";

interface Props {
  meetingSubtype?: MeetingSubtype;
  onUpdateMeeting: (data: Partial<IMeetingUpdatePayload>) => void;
}

const meetingSubtypeConfig = {
  [MeetingSubtype.CALL]: {
    label: MeetingSubtype.CALL,
    icon: VideoIcon,
    className: "text-emerald-400",
  },
  [MeetingSubtype.IN_PERSON]: {
    label: MeetingSubtype.IN_PERSON,
    icon: UserIcon,
    className: "text-sky-600",
  },
} satisfies Record<MeetingSubtype, { label: string; icon: React.ElementType; className: string }>;

export function MeetingSubtypeButtonSelector({ meetingSubtype, onUpdateMeeting }: Props) {
  const subtype = meetingSubtype || MeetingSubtype.CALL;
  const bgClassName = subtype === MeetingSubtype.CALL ? "bg-emerald-100" : "bg-sky-100";
  const { icon: Icon, label, className: iconClassName } = meetingSubtypeConfig[subtype];

  const subtypeOptions = useMemo(() => {
    return Object.values(meetingSubtypeConfig).map(({ label, icon, className }) => ({
      value: label,
      label,
      icon,
      className,
    }));
  }, []);

  const handleUpdateMeeting = (data: Partial<IMeetingUpdatePayload>) => onUpdateMeeting(data);

  return (
    <ButtonSelectorWithCustomSelect
      menuWidth={130}
      badgeClassName={cn(
        "h-[36px] animate-fade-in overflow-hidden text-ellipsis whitespace-nowrap",
        bgClassName
      )}
      label={
        <div className="flex items-center gap-1 dark:text-invertedPrimary80">
          <Icon className={iconClassName} />
          <span className="text-sm font-medium">{label}</span>
        </div>
      }
    >
      <MeetingSubtypeSelect
        options={subtypeOptions}
        onChange={(option) => handleUpdateMeeting({ subtype: option?.value })}
      />
    </ButtonSelectorWithCustomSelect>
  );
}
